import { useEffect, useRef, useState } from 'react';
import { TICKER_KEY, TICKER_EVENT, tickerOn } from '../utils/homeSettings';
import './home/home.css';

function lsEmailKey(base) {
  const email = localStorage.getItem('terminal_email') || '';
  return email ? `${base}_${email}` : base;
}

function readKey(base) {
  try { return JSON.parse(localStorage.getItem(lsEmailKey(base))); } catch { return null; }
}

/* Downloads the device-local data (watchlist, portfolio, trade log,
   settings) as JSON. Also used by the trial-expired overlay, which covers
   this bar. */
export function exportLocalData() {
  const email = localStorage.getItem('terminal_email') || '';
  const today = new Date().toISOString().slice(0, 10);
  const data = {
    export_date: today,
    email,
    version: '1.0',
    watchlist: readKey('local_watchlist') ?? [],
    portfolio: readKey('local_portfolio') ?? [],
    trade_log: readKey('local_tradelog') ?? [],
    settings:  readKey('local_settings')  ?? {},
  };
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `kripto-terminal-backup-${today}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

/* „Helyi mentés aktív” (3a): kis ikon a fejlécben, magyarázó tooltippel; a
   menüben az Exportálás / Importálás és az árszalag kapcsolója. */
export function LocalSaveMenu() {
  const email = localStorage.getItem('terminal_email') || '';
  const fileInputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [ticker, setTicker] = useState(tickerOn);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('mousedown', close); window.addEventListener('keydown', esc);
    return () => { window.removeEventListener('mousedown', close); window.removeEventListener('keydown', esc); };
  }, [open]);

  const toggleTicker = () => {
    const next = !ticker;
    try { localStorage.setItem(TICKER_KEY, String(next)); } catch { /* private mode */ }
    setTicker(next);
    window.dispatchEvent(new Event(TICKER_EVENT));
  };

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data.watchlist) || !Array.isArray(data.portfolio)) {
          alert('❌ Érvénytelen fájl formátum');
          return;
        }
        const k = email ? `_${email}` : '';
        localStorage.setItem(`local_watchlist${k}`, JSON.stringify(data.watchlist));
        localStorage.setItem(`local_portfolio${k}`, JSON.stringify(data.portfolio));
        if (Array.isArray(data.trade_log))
          localStorage.setItem(`local_tradelog${k}`, JSON.stringify(data.trade_log));
        if (data.settings && typeof data.settings === 'object')
          localStorage.setItem(`local_settings${k}`, JSON.stringify(data.settings));
        alert('✅ Adatok sikeresen importálva!');
        window.location.reload();
      } catch {
        alert('❌ Érvénytelen fájl formátum');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  return (
    <div className="lsm" ref={wrapRef}>
      <button type="button" className="lsm-btn" aria-haspopup="menu" aria-expanded={open}
        title="Helyi mentés aktív: a figyelőlista, a portfólió, a trade napló és a beállítások csak ezen az eszközön vannak tárolva."
        onClick={() => setOpen(o => !o)}>
        <span aria-hidden="true">💾</span><span className="lsm-dot" aria-hidden="true" />
        <span className="sr-only">Helyi mentés és beállítások</span>
      </button>
      {open && (
        <div className="lsm-menu" role="menu">
          <div className="lsm-info"><strong>Helyi mentés aktív</strong><br />Adataid (figyelőlista, portfólió, trade napló, beállítások) csak ezen az eszközön vannak tárolva. Mentsd ki, ha másik gépen is használnád.</div>
          <button type="button" role="menuitem" onClick={() => { exportLocalData(); setOpen(false); }}>⬆ Exportálás</button>
          <button type="button" role="menuitem" onClick={() => fileInputRef.current?.click()}>⬇ Importálás</button>
          <div className="lsm-sep" />
          <label className="lsm-check">
            <input type="checkbox" checked={ticker} onChange={toggleTicker} /> Árszalag a kezdőlap alján
          </label>
        </div>
      )}
      <input ref={fileInputRef} type="file" accept=".json" style={{ display: 'none' }} onChange={handleFileChange} />
    </div>
  );
}

export default function LocalStatusBar() {
  const email = localStorage.getItem('terminal_email') || '';
  const fileInputRef = useRef(null);
  const handleExport = exportLocalData;

  function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => {
      try {
        const data = JSON.parse(ev.target.result);
        if (!Array.isArray(data.watchlist) || !Array.isArray(data.portfolio)) {
          alert('❌ Érvénytelen fájl formátum');
          return;
        }
        const k = email ? `_${email}` : '';
        localStorage.setItem(`local_watchlist${k}`, JSON.stringify(data.watchlist));
        localStorage.setItem(`local_portfolio${k}`, JSON.stringify(data.portfolio));
        if (Array.isArray(data.trade_log))
          localStorage.setItem(`local_tradelog${k}`, JSON.stringify(data.trade_log));
        if (data.settings && typeof data.settings === 'object')
          localStorage.setItem(`local_settings${k}`, JSON.stringify(data.settings));
        alert('✅ Adatok sikeresen importálva!');
        window.location.reload();
      } catch {
        alert('❌ Érvénytelen fájl formátum');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  }

  return (
    <div className="local-status-bar">
      <div className="lsb-left">
        <span className="lsb-dot" />
        <span className="lsb-label">💾 Helyi mentés aktív</span>
        <span className="lsb-sub">Adataid csak ezen az eszközön vannak tárolva</span>
      </div>
      <div className="lsb-right">
        <button className="lsb-btn" onClick={handleExport}>⬆ Exportálás</button>
        <button className="lsb-btn" onClick={() => fileInputRef.current?.click()}>⬇ Importálás</button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
}
